import clsx from "clsx";

export interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Should the button have a background color or a border?
   */
  variant?: "contained" | "outlined";
  /**
   * How large should the button be?
   */
  size?: "small" | "medium" | "large";
  /**
   * Color of the button
   */
  color?: "primary" | "secondary" | "danger" | "gray";
  /**
   * Should the button text be upper-case?
   */
  upperCase?: boolean;
  /**
   * Should the button be BOLD?
   */
  bold?: boolean;
  /**
   * Should the button be raised? (like Material UI?)
   */
  shadow?: boolean;
  /**
   * Show Loader? If loader's shown, the button is disabled as well.
   */
  loading?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 *
 * Main Button Component. Passes all native button props down to the button.
 */
const Button = ({
  variant = "contained",
  size = "medium",
  color,
  upperCase = true,
  bold = true,
  shadow = true,
  loading = false,
  disabled,
  children,
  className,
  ...props
}: Props) => {
  const colorPassed = className?.includes("bg-");
  if (!color && !colorPassed) {
    color = "primary";
  }
  return (
    <button
      disabled={loading || disabled}
      {...props}
      className={clsx(
        className,
        "p-2",
        "m-1",
        "rounded",
        "elevation-4, hover:elevation-8",
        (loading || disabled) && "cursor-not-allowed",
        loading && "inline-flex justify-center",
        upperCase && "uppercase",
        bold && "font-semibold",
        size === "small" && "text-sm",
        size === "large" && "text-lg",
        shadow && "shadow hover:shadow-2xl",
        variant === "outlined" && "bg-transparent border",
        color === "primary" &&
          (variant === "contained"
            ? disabled
              ? "bg-primary-light text-primary-contrastText"
              : "bg-primary-main text-primary-contrastText"
            : "text-primary-main border-primary-main hover:text-primary-contrastText hover:bg-primary-dark"),
        color === "secondary" &&
          (variant === "contained"
            ? disabled
              ? "bg-secondary-light text-gray-700"
              : "bg-secondary-dark text-secondary-contrastText"
            : "text-secondary-dark border-secondary-dark hover:text-secondary-contrastText hover:bg-secondary-dark"),
        color === "danger" &&
          (variant === "contained"
            ? "bg-error-main text-error-contrastText"
            : "text-error-main border-error-main hover:text-error-contrastText hover:bg-error-dark"),
        color === "gray" &&
          (variant === "contained"
            ? "bg-gray-300 text-gray-700"
            : "text-gray-600 border-gray-300 hover:text-gray-100 hover:bg-gray-700")
      )}
    >
      {loading && (
        <svg
          className="animate-spin h-5 w-5 mr-3  text-white"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {children}
    </button>
  );
};

export default Button;
